import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { VscClose, VscChevronLeft, VscChevronRight, VscTrash, VscLinkExternal, VscAdd } from 'react-icons/vsc';
import { BsImages, BsCamera } from 'react-icons/bs';
import { uploadPhotos, deletePhoto } from '../../api/photos';
import { downscaleImage } from '../../utils/imageResize';
import './PhotoGallery.css';

const WRITE_ROLES = ['admin', 'engineer', 'manager', 'foreman'];

// Modal viewer for the photos of an Item or Cable. kind: 'items' | 'cables'
function PhotoGallery({ kind, record, title, role, onPhotosChange, onClose }) {
  const photos = record.photos || [];
  const [idx, setIdx]             = useState(0);
  const [uploading, setUploading] = useState(0);
  const [error, setError]         = useState('');
  const fileRef = useRef(null);
  const rootRef = useRef(null);

  const me     = localStorage.getItem('kspro_userId');
  const canAdd = role !== 'client';
  const cur    = photos[Math.min(idx, photos.length - 1)];
  const canDelete = cur && (WRITE_ROLES.includes(role) || (cur.uploadedBy?._id || cur.uploadedBy) === me);

  useEffect(() => { rootRef.current?.focus(); }, []);

  const go = (d) => photos.length && setIdx(i => (i + d + photos.length) % photos.length);

  const handleFiles = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    if (!files.length) return;
    setError(''); setUploading(files.length);
    try {
      const prepared = await Promise.all(files.map(f => downscaleImage(f)));
      const res = await uploadPhotos(kind, record._id, prepared);
      onPhotosChange(res.data);
      setIdx(photos.length);   // jump to the first new photo
    } catch (err) {
      setError(err.response?.data?.msg || 'Upload failed.');
    } finally {
      setUploading(0);
      rootRef.current?.focus();
    }
  };

  const handleDelete = async () => {
    if (!cur || !confirm('Delete this photo?')) return;
    try {
      const res = await deletePhoto(kind, record._id, cur._id);
      onPhotosChange(res.data);
      setIdx(i => Math.max(0, Math.min(i, res.data.length - 1)));
    } catch (err) {
      setError(err.response?.data?.msg || 'Delete failed.');
    }
  };

  // Rendered in a portal, but React events still bubble to the table row underneath —
  // stop them so clicks/keys here don't start row edits or table navigation
  const onKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Escape')     { e.preventDefault(); onClose(); }
    if (e.key === 'ArrowLeft')  { e.preventDefault(); go(-1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
  };

  const who  = cur?.uploadedBy?.displayName || cur?.uploadedBy?.username;
  const when = cur?.uploadedAt && new Date(cur.uploadedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

  return createPortal(
    <div className="photo-backdrop" ref={rootRef} tabIndex={-1} onKeyDown={onKeyDown}
      onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}>
      <div className="photo-modal" role="dialog" aria-label={`Photos — ${title}`}>
        <div className="photo-header">
          <span className="photo-title">Photos — {title}</span>
          {photos.length > 0 && <span className="photo-count">{Math.min(idx, photos.length - 1) + 1} / {photos.length}</span>}
          <span style={{ flex: 1 }} />
          {canAdd && (
            <button className="btn-primary photo-add-btn" onClick={() => fileRef.current?.click()} disabled={!!uploading}>
              <VscAdd size={14} />{uploading ? `Uploading ${uploading}…` : 'Add photos'}
            </button>
          )}
          <button className="btn-icon" title="Close (Esc)" onClick={onClose}><VscClose size={18} /></button>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handleFiles} />
        </div>

        {error && <div className="row-error">{error}</div>}

        {cur ? (
          <>
            <div className="photo-stage">
              <img key={cur._id} src={cur.url} alt={cur.name || ''} />
              {photos.length > 1 && (
                <>
                  <button className="photo-nav photo-nav-prev" title="Previous (←)" onClick={() => go(-1)}><VscChevronLeft size={28} /></button>
                  <button className="photo-nav photo-nav-next" title="Next (→)" onClick={() => go(1)}><VscChevronRight size={28} /></button>
                </>
              )}
            </div>
            <div className="photo-meta">
              <span className="photo-meta-text">
                {[who, when].filter(Boolean).join(' · ')}
              </span>
              <a className="btn-icon" href={cur.url} target="_blank" rel="noreferrer" title="Open full size"><VscLinkExternal size={14} /></a>
              {canDelete && <button className="btn-icon btn-icon-danger" title="Delete photo" onClick={handleDelete}><VscTrash size={14} /></button>}
            </div>
            {photos.length > 1 && (
              <div className="photo-thumbs">
                {photos.map((p, i) => (
                  <button key={p._id} className={`photo-thumb${p === cur ? ' active' : ''}`} onClick={() => setIdx(i)}>
                    <img src={p.url} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="photo-empty">
            <BsImages size={36} />
            <span>{uploading ? 'Uploading…' : 'No photos yet.'}</span>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// Row button: image icon (with count) when photos exist; a faint camera on row hover
// when there are none, so photos can be added. Opens the gallery.
export function PhotoButton({ kind, record, title, role, onPhotosChange }) {
  const [open, setOpen] = useState(false);
  const count = record.photos?.length || 0;
  if (!count && role === 'client') return null;
  return (
    <>
      <button
        className={`btn-icon photo-btn${count ? ' has-photos' : ' photo-btn-empty'}`}
        title={count ? `${count} photo${count > 1 ? 's' : ''}` : 'Add photos'}
        onClick={e => { e.stopPropagation(); setOpen(true); }}
      >
        {count ? <BsImages size={13} /> : <BsCamera size={13} />}
        {count > 1 && <span className="photo-btn-count">{count}</span>}
      </button>
      {open && (
        <PhotoGallery kind={kind} record={record} title={title} role={role}
          onPhotosChange={onPhotosChange} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

export default PhotoGallery;
